// frontend/src/components/Layout.jsx
// PHASE 4: SSE temps réel branché ici (niveau global, une seule connexion pour toute l'app)
import React, { useState, useEffect, useRef } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import "../styles/main.css";
import MarketTicker from "./MarketTicker";
import AdminNav from "./AdminNav";
import quantumBg from "../assets/quantum-pass.png";
import { connectSSE, disconnectSSE } from "../services/sseService";
import { useAuth } from "../context/AuthContext";
import CookieBanner from "./CookieBanner";

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { authenticated, isAdmin, onLogout } = useAuth();
  const [sseStatus, setSSEStatus] = useState("OFFLINE");
  const sseStarted = useRef(false);

  const isActive = (path) => location.pathname === path;
  const isHomePage = location.pathname === "/hub";

  // ─── PHASE 4: Connexion SSE globale (une seule instance pour toute l'app) ──
  useEffect(() => {
    if (sseStarted.current) return;  // Protection contre les double-montages React Strict Mode
    sseStarted.current = true;

    connectSSE({
      // sigma_sealed → met à jour le store Zustand → les cartes passent en VERT sans F5
      onSigmaSealed: (payload) => {
        try {
          // Import dynamique du store pour éviter les dépendances circulaires
          import("../store/useNexusStore").then(({ default: useNexusStore }) => {
            const store = useNexusStore.getState();
            // sealDecision(decisionId, sealHash) — met à jour la carte en VERT
            if (typeof store.sealDecision === 'function') {
              store.sealDecision(payload.decisionId, payload.sealHash);
            }
            // Fallback: si le store expose setDecisionSealed ou updateDecision
            else if (typeof store.setDecisionSealed === 'function') {
              store.setDecisionSealed(payload.decisionId, payload.sealHash, payload.oracleConfirmation);
            }
            console.info(`[Layout SSE] ✅ Carte ${payload.decisionId} → SCELLÉE (hash: ${payload.sealHash?.slice(0, 8)}...)`);
          }).catch((err) => {
            console.warn('[Layout SSE] Store non disponible:', err);
          });
        } catch (err) {
          console.error('[Layout SSE] Erreur onSigmaSealed:', err);
        }
      },

      onStatusChange: (status) => {
        setSSEStatus(status);
        console.info(`[Layout SSE] Status → ${status}`);
      },

      onError: (err) => {
        console.warn('[Layout SSE] Erreur de connexion:', err);
      },
    });

    return () => {
      disconnectSSE();
      sseStarted.current = false;
    };
  }, []); // [] → une seule fois au montage du Layout global

  // ✅ Routes admin : pas de navbar publique (AdminNav suffit)
  const adminPaths = ["/admin", "/icosahedron", "/cubes", "/spiral-simple", "/spiral-torus",
    "/stream", "/torus-spring", "/centrifuge-laser", "/centrifuge-laser-v2",
    "/crypto-token-river", "/metacube-oracle", "/metacube-oracle-v2", "/metacube-oracle-v3",
    "/metacube-oracle-v4", "/metacube-oracle-v5", "/metacube-quantique",
    "/legacy-token-gen", "/blockchain", "/blockchain-dashboard", "/token-price"];
  const isAdminRoute = adminPaths.some(p => location.pathname.startsWith(p));

  const showAdminNav = !isHomePage && isAdmin;
  // Masquer la navbar publique sur les routes admin (AdminNav suffit)
  const showPublicNav = !isHomePage && !isAdminRoute;

  // Couleur de l'indicateur SSE
  const sseColor = {
    LIVE: '#00ff88',
    CONNECTING: '#ffaa00',
    RECONNECTING: '#ff6600',
    OFFLINE: '#ff3333',
  }[sseStatus] ?? '#666';

  const handleLogout = async () => {
    await onLogout();
    navigate("/login");
  };

  return (
    <div className="app-container">
      {/* GLOBAL BACKGROUND */}
      {!isHomePage && (
        <div style={{
          position: "fixed",
          top: 0, left: 0,
          width: "100%", height: "100%",
          zIndex: 0,
          background: `black url(${quantumBg}) no-repeat center center fixed`,
          backgroundSize: "cover"
        }} />
      )}

      <header className="main-header">
        {showAdminNav && <AdminNav />}
        <MarketTicker />

        {/* PHASE 4: Indicateur SSE temps réel (visible uniquement pour l'admin) */}
        {isAdmin && (
          <div style={{
            position: 'fixed',
            bottom: '12px',
            right: '16px',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'rgba(0,0,0,0.75)',
            border: `1px solid ${sseColor}`,
            borderRadius: '20px',
            padding: '4px 10px',
            fontSize: '11px',
            color: sseColor,
            fontFamily: 'monospace',
            backdropFilter: 'blur(4px)',
          }}>
            <span style={{
              width: '7px', height: '7px',
              borderRadius: '50%',
              background: sseColor,
              boxShadow: sseStatus === 'LIVE' ? `0 0 6px ${sseColor}` : 'none',
              animation: sseStatus === 'LIVE' ? 'none' : 'pulse 1s infinite',
            }} />
            SSE {sseStatus}
          </div>
        )}
      </header>

      {/* ✅ BARRE DE NAVIGATION INTELLIGENTE 
          On ne l'affiche PAS sur la Home (isHomePage) pour éviter les doublons.
          On l'affiche sur TOUTES les autres pages pour pouvoir naviguer.
      */}
      {showPublicNav && (
        <nav className="navbar">
          <Link to="/" className="nav-button" style={{ border: "1px solid #666" }}>
            🏠 Accueil
          </Link>
          <Link to="/token-generator-dashboard" className={`nav-button ${isActive("/token-generator-dashboard") ? "active" : ""}`}>
            🎲 Token
          </Link>
          <Link to="/entropy-dashboard" className={`nav-button ${isActive("/entropy-dashboard") ? "active" : ""}`}>
            ⚡ Entropy
          </Link>
          <Link to="/web3-wallet" className={`nav-button ${isActive("/web3-wallet") ? "active" : ""}`}>
            💼 Wallet
          </Link>

          {/* ✅ Bouton Public : Gouvernance */}
          <Link
            to="/governance"
            className={`nav-button ${isActive("/governance") ? "active" : ""}`}
            style={{ color: "#00E0FF", borderColor: "rgba(0,224,255,0.3)" }}
          >
            🏛️ Gouvernance
          </Link>

          {/* 🛡️ Bouton Admin : Sentinel Control Room */}
          {isAdmin && (
            <Link
              to="/v2/dashboard"
              className="nav-button"
              style={{
                color: "#00FF94",
                borderColor: "rgba(0,255,148,0.4)",
                boxShadow: "0 0 10px rgba(0,255,148,0.2)",
                fontWeight: "bold"
              }}
            >
              🛡️ SENTINEL
            </Link>
          )}

          <Link
            to="/quid"
            className={`nav-button ${isActive("/quid") ? "active" : ""}`}
            style={{ color: "#00ff", borderColor: "rgba(0,255,255,0.3)" }}
          >
            🏷️ QUID
          </Link>

          {/* 🚀 Portail client B2B — accès permanent à l'espace pro */}
          <Link
            to="/portal/register"
            className={`nav-button ${location.pathname.startsWith("/portal") ? "active" : ""}`}
            style={{ color: "#c9a4ff", borderColor: "rgba(165,117,235,0.5)", boxShadow: "0 0 10px rgba(165,117,235,0.3)" }}
          >
            🚀 Portail API
          </Link>

          {authenticated && (
            <button
              onClick={handleLogout}
              className="nav-button"
              style={{
                color: "#ff4444",
                borderColor: "rgba(255, 68, 68, 0.3)",
                background: "transparent",
                cursor: "pointer",
                marginLeft: "auto"
              }}
              onMouseOver={(e) => {
                e.target.style.background = "rgba(255, 68, 68, 0.1)";
              }}
              onMouseOut={(e) => {
                e.target.style.background = "transparent";
              }}
            >
              🚪 Se déconnecter
            </button>
          )}
        </nav>
      )}

      <main className="page-content" style={{ position: "relative", zIndex: 1 }}>
        {children || <Outlet />}
      </main>

      <footer style={{
        textAlign: "center",
        padding: "20px",
        borderTop: "1px solid #333",
        marginTop: "40px",
        color: "#666",
        fontSize: "0.8rem",
        background: "#000"
      }}>
        <div style={{ display: "flex", justifyContent: "center", gap: "20px" }}>
          <Link to="/privacy" style={{ color: "#888", textDecoration: "none" }}>Politique de Confidentialité</Link>
          <Link to="/terms" style={{ color: "#888", textDecoration: "none" }}>Conditions d'Utilisation</Link>
          <Link to="/retention-policy" style={{ color: "#888", textDecoration: "none" }}>Politique de Rétention</Link>
        </div>
        <div style={{ marginTop: "10px" }}>
          &copy; {new Date().getFullYear()} Quantum Unique Identifier. All rights reserved.
        </div>
      </footer>

      {/* ✅ Bannière Cookies de consentement RGPD */}
      <CookieBanner />
    </div>
  );
}
